import { Type } from 'class-transformer';
import { LinkedIntegration } from './linked_integration';
import { Model } from './model';
import dayjs from 'dayjs';
import { TransformStringToDayJS } from './utils';
import { PaymentSettings } from './payment_settings';

export enum OrganizationOnboardingStepStatus {
  PENDING = 'pending',
  SKIPPED = 'skipped',
  COMPLETED = 'completed',
}

export enum OrganizationOnboardingUsecase {
  COLLECTIONS = 'collections',
  CASHAPPLICATION = 'cash_application',
  PAYMENTS = 'payments',
  CREDIT = 'credit',
}

export enum OrganizationOnboardingStep {
  WELCOME = 'welcome',
  GOALS = 'goals',
  SYNCINVOICES = 'sync_invoices',
  EMAILS = 'emails',
  CALLS = 'calls',
  KNOWLEDGE = 'knowledge_entries',
  SUMMARY = 'summary',
}

// ordered organization onboarding steps
export const ONBOARDING_STEPS: OrganizationOnboardingStep[] = [
  OrganizationOnboardingStep.WELCOME,
  OrganizationOnboardingStep.GOALS,
  // OrganizationOnboardingStep.SYNCINVOICES,
  OrganizationOnboardingStep.EMAILS,
  OrganizationOnboardingStep.CALLS,
  // OrganizationOnboardingStep.KNOWLEDGE,
  OrganizationOnboardingStep.SUMMARY,
];

export class OrganizationOnboardingStepData {
  is_required: boolean;

  status: OrganizationOnboardingStepStatus;
}

export interface PaymentProcessingDaysConfig {
  ach: number;

  check: number;

  other: number;

  wire: number;
}

export class Organization extends Model {
  id: string;

  bank_statement_extraction_guidelines: string | null;

  display_name: string;

  name: string;

  @Type(() => OrganizationDomain)
  domains: OrganizationDomain[];

  @Type(() => LinkedIntegration)
  integrations: LinkedIntegration[];

  agent_phone_number: string;

  auto_provision_accounts_in_domain: boolean;

  currency: string;

  customer_support_number: string;

  customer_support_email: string;

  outreach_phone_number: string;

  ar_team_size: string;

  auto_send_invoice_amount_limit: number | null;

  invoices_per_month: string;

  default_outreach_window_start: string;

  default_outreach_window_end: string;

  erp_software: string;

  crm_software: string;

  broken_promise_grace_days: number;

  email_software: string;

  email_provider: 'nylas' | 'sendgrid';

  hybrid_mode_enabled: boolean;

  enterprise_auth_connection_name: string | null;

  is_organization_registered: boolean;

  is_onboarding_complete: boolean;

  is_onboarding_disabled: boolean;

  is_password_auth_enabled: boolean;

  is_disputes_enabled: boolean = false;

  inbound_grace_period_days: number;

  is_broken_promise_follow_up_enabled: boolean;

  is_enterprise_auth_enabled: boolean;

  is_always_set_ptp_enabled: boolean;

  is_auto_set_primary_enabled: boolean;

  is_auto_send_email_enabled: boolean;

  is_auto_send_sms_enabled: boolean;

  is_base_disputes_enabled: boolean;

  is_call_concurrency_enabled: boolean;

  is_call_dedup_enabled: boolean;

  is_cash_app_enabled: boolean;

  is_deductions_enabled: boolean;

  is_sandbox: boolean;

  is_sms_outreach_enabled: boolean;

  is_vaulting_enabled: boolean;

  has_business_units: boolean;

  is_parent_partner_outreach_enabled: boolean;

  is_partial_payment_outreach_enabled: boolean;

  is_auto_approve_tasks_enabled: boolean;

  is_auto_dunning_state_enabled: boolean;

  is_customer_level_dispute_pause_enabled: boolean = false;

  max_overdue_days_for_triggers: number | null;

  payment_processing_days_config: PaymentProcessingDaysConfig;

  @TransformStringToDayJS()
  last_payment_export_at: dayjs.Dayjs | null;

  payment_software: string;

  payment_settings: PaymentSettings | null;

  processes_improving_with_ai: string[];

  remittance_inbox: string;

  timezone: string;

  recording_consent_required: boolean;

  logo_url: string | null;

  onboarding_steps: Record<OrganizationOnboardingStep, OrganizationOnboardingStepData>;

  onboarding_usecases: Record<OrganizationOnboardingUsecase, boolean>;
}

export class OrganizationDomain {
  domain: string;
}
